import * as RadixPopover from '@radix-ui/react-popover';
import type { ReactNode } from 'react';
import { cx } from '../lib/cx';

/**
 * An anchored panel that closes on outside click, on Escape, and on a route
 * change.
 *
 * ── What this replaces ──────────────────────────────────────────────────────
 * Four overlays, three implementations, and two of them could not be dismissed
 * from the keyboard at all:
 *
 *   Modal.tsx            Radix Dialog. Correct already; a dialog, not this.
 *   AppShell (search)    a hand-written useEffect pair binding pointerdown and
 *                        keydown on document.
 *   UserMenu             the SAME useEffect pair, copied verbatim.
 *   NotificationBell     an invisible full-screen <button> as a click-catcher,
 *                        with no Escape handler.
 *   AttachmentStrip      a lightbox div with role="presentation" and onClick,
 *                        with no Escape handler.
 *
 * The invisible full-screen button is the one worth naming: it is focusable and
 * announces to a screen reader as "Close notifications, button" covering the
 * entire viewport, so it was an accessibility problem as well as a duplicate.
 *
 * `@radix-ui/react-popover` was already a dependency and was imported nowhere.
 * It solves all of it — focus management, Escape, outside-click, portalling
 * out of clipping ancestors, `aria-expanded` and `aria-controls` wired to the
 * trigger — and Spec v0.5 §5.4 names exactly this as the reason to use Radix:
 * "the genuinely hard accessibility problems".
 */

export interface PopoverProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** The control the panel hangs off. Radix wires the ARIA onto it. */
  readonly trigger: ReactNode;
  readonly children: ReactNode;
  /** Accessible name for the panel itself. */
  readonly label: string;
  readonly align?: 'start' | 'center' | 'end';
  readonly className?: string;
}

export function Popover({
  open,
  onOpenChange,
  trigger,
  children,
  label,
  align = 'end',
  className,
}: PopoverProps) {
  return (
    <RadixPopover.Root open={open} onOpenChange={onOpenChange}>
      {/* asChild so the caller's own button IS the trigger, rather than being
          wrapped in one — a button inside a button is invalid, and the wrapper
          would take the focus ring. */}
      <RadixPopover.Trigger asChild>{trigger}</RadixPopover.Trigger>
      <RadixPopover.Portal>
        <RadixPopover.Content
          align={align}
          sideOffset={8}
          aria-label={label}
          className={cx(
            'z-30 overflow-hidden rounded-xl border border-line bg-base shadow-lg',
            'focus:outline-none',
            className,
          )}
        >
          {children}
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  );
}
